import {SelectAsync} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useMemo} from 'react';
import {useParams} from 'react-router';
import {type SingleValue} from 'react-select';

import {
  mediaSliderMetricKeyRemoved,
  mediaSliderMetricKeySelected,
} from '../../../../../services/analytics/productEvents';
import {useViewer} from '../../../../../state/viewer/hooks';
import {keyDisplayName} from '../../../../../util/runs';
import {useIsNgwEnabledInWorkspace} from '../../../../MetricSearch/useIsNgwEnabledInWorkspace';
import {type MediaBrowserPanelConfig} from '../../../../PanelMediaBrowser/common';
import {useMediaKeys} from '../../../../PanelMediaBrowser/hooks/useMediaKeys';
import {clearColumnValue} from '../../../../PanelMediaBrowser/layout/contexts/compareConfigUtils';
import {
  useMediaCompareSettingsDisplay,
  useMediaSliderKeyDisplay,
  useShouldShowMediaSliderKeyResetAtLevel,
} from '../../hooks';
import {SettingTitle} from '../common/SettingTitle';
import {useMediaSettingsContext} from '../contexts/MediaSettingsContextProvider';
import {SliderKeyMetricSearch} from './SliderKeyMetricSearch';

type SliderKeyOption = {
  value: string | undefined;
  displayText: string;
};
const MAX_OPTIONS_DISPLAYED = 10;

export const SliderKey = () => {
  const {updateConfig, unsetConfigField, settingsLevel, panelRef} =
    useMediaSettingsContext();
  const currentMediaSliderKey = useMediaSliderKeyDisplay(
    settingsLevel,
    panelRef
  );
  const isSetAtLevel = useShouldShowMediaSliderKeyResetAtLevel(
    settingsLevel,
    panelRef
  );
  const compareSettings = useMediaCompareSettingsDisplay(
    settingsLevel,
    panelRef
  );

  // Tracking events related to the slider key
  const {entityName, projectName, panelId} = useParams<{
    entityName: string;
    projectName: string;
    panelId: string;
  }>();
  const user = useViewer()?.username;
  const trackMediaSliderKeyChange = useCallback(
    (updatedValue: string) => {
      const isCurrentValueDefault = currentMediaSliderKey === '_step';
      const isNewValueDefault = updatedValue === '_step';
      const eventData = {
        entityName,
        projectName,
        user,
        panelId,
      };
      if (!isNewValueDefault) {
        mediaSliderMetricKeySelected({
          ...eventData,
          metricKey: updatedValue,
        });
      } else if (!isCurrentValueDefault && isNewValueDefault) {
        mediaSliderMetricKeyRemoved({
          previousMetricKey: currentMediaSliderKey,
          ...eventData,
        });
      }
    },
    [currentMediaSliderKey, entityName, projectName, panelId, user]
  );

  const onSliderKeyChange = useCallback(
    function applySliderKeyChange(updatedValue: string) {
      trackMediaSliderKeyChange(updatedValue);
      const updates: Partial<MediaBrowserPanelConfig> = {
        mediaSliderKey: updatedValue,
        stepIndex: undefined,
      };
      const configCompareSettings = compareSettings ?? {};
      const newCompareSettings = clearColumnValue(
        configCompareSettings,
        'step'
      );
      if (configCompareSettings !== newCompareSettings) {
        updates.compareSettings = newCompareSettings;
      }
      // clearing the step index to reset the step to the max value for the new key
      updateConfig(updates);
    },
    [trackMediaSliderKeyChange, compareSettings, updateConfig]
  );

  // The two pickers are separate components so neither calls the other's hooks:
  // the metric-search one reads WorkspaceRefsContext, the legacy one reads key
  // info that NGW leaves empty. Same split as MediaKeys.
  const useMetricSearch = useIsNgwEnabledInWorkspace();

  return (
    <div className="flex flex-col">
      <SettingTitle
        title="Slider key"
        configField="mediaSliderKey"
        isDirty={isSetAtLevel}
        unsetConfigField={() => unsetConfigField('mediaSliderKey')}
      />
      {useMetricSearch ? (
        <SliderKeyMetricSearch
          currentMediaSliderKey={currentMediaSliderKey}
          onSliderKeyChange={onSliderKeyChange}
        />
      ) : (
        <SliderKeyFromKeyInfo
          currentMediaSliderKey={currentMediaSliderKey}
          onSliderKeyChange={onSliderKeyChange}
        />
      )}
    </div>
  );
};

const SliderKeyFromKeyInfo = ({
  currentMediaSliderKey,
  onSliderKeyChange,
}: {
  currentMediaSliderKey: string;
  onSliderKeyChange: (value: string) => void;
}) => {
  const {numericKeys} = useMediaKeys();
  const sliderKeyOptions: SliderKeyOption[] = useMemo(
    function buildSliderKeyOptions() {
      return (
        numericKeys?.map(k => ({
          value: k,
          displayText: keyDisplayName({section: 'summary', name: k}),
        })) ?? []
      );
    },
    [numericKeys]
  );

  const filteredSliderKeyOptions = useCallback(
    function filterSliderKeyOptions(
      inputValue: string,
      callback: (options: SliderKeyOption[]) => void
    ) {
      const filtered = sliderKeyOptions
        .filter(k =>
          k.displayText.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, MAX_OPTIONS_DISPLAYED);
      callback(filtered);
    },
    [sliderKeyOptions]
  );
  const selectedOption = sliderKeyOptions.find(
    k => k.value === currentMediaSliderKey
  );

  return (
    <SelectAsync<SliderKeyOption, false>
      aria-label="Select Slider Key"
      cacheOptions
      defaultOptions
      options={sliderKeyOptions}
      loadOptions={filteredSliderKeyOptions}
      formatOptionLabel={({displayText}) => (
        <div>
          <span>{displayText}</span>
        </div>
      )}
      isSearchable
      value={selectedOption}
      onChange={(selection: SingleValue<SliderKeyOption>) => {
        onSliderKeyChange(selection?.value ?? '_step');
      }}
    />
  );
};
